import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

const TroubleshootingFAQ = () => {
  const [activeCategory, setActiveCategory] = useState('installation');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFAQs, setExpandedFAQs] = useState(new Set());

  // Animate cards on scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });

    const cards = document.querySelectorAll('.help-card, .stat-card, .faq-item');
    cards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      card.style.transition = 'all 0.6s ease-out';
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, [activeCategory, searchTerm]);

  const showCategory = (categoryId) => {
    setActiveCategory(categoryId);
    setExpandedFAQs(new Set()); // Reset expanded FAQs when changing category
  };

  const toggleFAQ = (faqId) => {
    setExpandedFAQs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(faqId)) {
        newSet.delete(faqId);
      } else {
        newSet.clear(); // Close other FAQs
        newSet.add(faqId);
      }
      return newSet;
    });
  };

  const filterFAQ = (term) => {
    setSearchTerm(term);
  };

  const setSearch = (term) => {
    setSearchTerm(term);
  };

  const copyCode = (button, codeText) => {
    navigator.clipboard.writeText(codeText).then(() => {
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      button.style.background = 'rgba(103, 232, 169, 0.3)';
      button.style.color = '#67e8a9';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
        button.style.color = '';
      }, 2000);
    });
  };

  const FAQItem = ({ id, severity, question, children }) => {
    const isExpanded = expandedFAQs.has(id);
    const isVisible = !searchTerm || 
      question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (typeof children === 'string' && children.toLowerCase().includes(searchTerm.toLowerCase()));

    if (!isVisible && searchTerm) return null;

    return (
      <div className={`faq-item ${severity}`}>
        <div className={`faq-question ${isExpanded ? 'active' : ''}`} onClick={() => toggleFAQ(id)}>
          <div className="faq-question-text">{question}</div>
          <div className={`faq-icon ${isExpanded ? 'expanded' : ''}`}>▼</div>
        </div>
        <div className={`faq-answer ${isExpanded ? 'expanded' : ''}`}>
          <div className="faq-answer-content">
            {children}
          </div>
        </div>
      </div>
    );
  };

  const CodeBlock = ({ lang, code }) => {
    return (
      <div className="code-block">
        <div className="code-header">
          <span className="code-lang">{lang}</span>
          <button className="copy-btn" onClick={(e) => copyCode(e.target, code)}>Copy</button>
        </div>
        <pre>{code}</pre>
      </div>
    );
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Search Bar */
        .search-section {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin-bottom: 3rem;
            text-align: center;
        }
        
        .search-container {
            position: relative;
            max-width: 600px;
            margin: 0 auto;
            transition: transform 0.3s;
        }
        
        .search-container:focus-within {
            transform: scale(1.02);
        }
        
        .search-input {
            width: 100%;
            padding: 1.2rem 1.5rem 1.2rem 3rem;
            background: rgba(255, 255, 255, 0.05);
            border: 2px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            color: white;
            font-size: 1.1rem;
            transition: all 0.3s;
        }
        
        .search-input:focus {
            outline: none;
            border-color: rgba(102, 126, 234, 0.5);
            background: rgba(255, 255, 255, 0.08);
        }
        
        .search-input::placeholder {
            color: #666;
        }
        
        .search-icon {
            position: absolute;
            left: 1rem;
            top: 50%;
            transform: translateY(-50%);
            color: #888;
            font-size: 1.2rem;
        }
        
        .search-suggestions {
            display: flex;
            gap: 1rem;
            justify-content: center;
            margin-top: 1.5rem;
            flex-wrap: wrap;
        }
        
        .search-tag {
            padding: 0.5rem 1rem;
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
            border-radius: 20px;
            color: #667eea;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 0.9rem;
        }
        
        .search-tag:hover {
            background: rgba(102, 126, 234, 0.2);
            transform: translateY(-2px);
        }
        
        /* Quick Help Cards */
        .quick-help {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1.5rem;
            margin-bottom: 4rem;
        }
        
        .help-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
            text-align: center;
            transition: all 0.3s;
            cursor: pointer;
        }
        
        .help-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
            background: rgba(255, 255, 255, 0.05);
        }
        
        .help-icon {
            font-size: 2.5rem;
            margin-bottom: 1rem;
        }
        
        .help-title {
            font-size: 1.1rem;
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
        
        .help-description {
            color: #888;
            font-size: 0.9rem;
        }
        
        /* Category Tabs */
        .category-tabs {
            display: flex;
            justify-content: center;
            gap: 1rem;
            margin-bottom: 3rem;
            flex-wrap: wrap;
        }
        
        .tab-button {
            padding: 0.8rem 2rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            color: #888;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 1rem;
            font-weight: 600;
        }
        
        .tab-button:hover {
            background: rgba(255, 255, 255, 0.1);
            color: white;
        }
        
        .tab-button.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-color: transparent;
        }
        
        /* FAQ Sections */
        .faq-section {
            display: none;
            animation: fadeIn 0.5s ease;
        }
        
        .faq-section.active {
            display: block;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        /* FAQ Items */
        .faq-container {
            margin: 2rem 0;
        }
        
        .faq-item {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            margin-bottom: 1rem;
            overflow: hidden;
            transition: all 0.3s;
        }
        
        .faq-item:hover {
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .faq-question {
            padding: 1.5rem;
            cursor: pointer;
            display: flex;
            justify-content: between;
            align-items: center;
            font-weight: 600;
            transition: all 0.3s;
        }
        
        .faq-question:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        .faq-question.active {
            background: rgba(102, 126, 234, 0.1);
            color: #667eea;
        }
        
        .faq-question-text {
            flex: 1;
        }
        
        .faq-icon {
            font-size: 1.2rem;
            color: #667eea;
            transition: transform 0.3s;
        }
        
        .faq-icon.expanded {
            transform: rotate(180deg);
        }
        
        .faq-answer {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.3s ease-out;
        }
        
        .faq-answer.expanded {
            max-height: 500px;
        }
        
        .faq-answer-content {
            padding: 0 1.5rem 1.5rem;
            color: #bbb;
        }
        
        .faq-answer-content h4 {
            color: #fff;
            margin: 1rem 0 0.5rem;
        }
        
        .faq-answer-content ul {
            margin: 0.5rem 0;
            padding-left: 2rem;
        }
        
        .faq-answer-content li {
            margin: 0.3rem 0;
        }
        
        .faq-answer-content code {
            background: rgba(255, 255, 255, 0.1);
            padding: 0.2rem 0.5rem;
            border-radius: 4px;
            font-family: monospace;
            color: #c792ea;
        }
        
        /* Code Blocks */
        .code-block {
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 8px;
            padding: 1rem;
            margin: 1rem 0;
            overflow-x: auto;
            position: relative;
        }
        
        .code-header {
            display: flex;
            justify-content: between;
            align-items: center;
            margin-bottom: 0.5rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid #333;
        }
        
        .code-lang {
            color: #667eea;
            font-size: 0.9rem;
            font-weight: 600;
        }
        
        .copy-btn {
            background: rgba(102, 126, 234, 0.2);
            border: 1px solid rgba(102, 126, 234, 0.3);
            color: #667eea;
            padding: 0.3rem 0.8rem;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.8rem;
            transition: all 0.3s;
        }
        
        .copy-btn:hover {
            background: rgba(102, 126, 234, 0.3);
        }
        
        .code-block pre {
            margin: 0;
            color: #e0e0e0;
            font-family: 'Consolas', 'Monaco', monospace;
            font-size: 0.9rem;
            line-height: 1.6;
        }
        
        /* Severity Indicators */
        .severity-critical {
            border-left: 4px solid #f56565;
            background: rgba(245, 101, 101, 0.1);
        }
        
        .severity-warning {
            border-left: 4px solid #ed8936;
            background: rgba(237, 137, 54, 0.1);
        }
        
        .severity-info {
            border-left: 4px solid #667eea;
            background: rgba(102, 126, 234, 0.1);
        }
        
        .severity-success {
            border-left: 4px solid #67e8a9;
            background: rgba(103, 232, 169, 0.1);
        }
        
        /* Troubleshooting Steps */
        .troubleshooting-steps {
            margin: 1.5rem 0;
        }
        
        .step {
            display: flex;
            align-items: start;
            gap: 1rem;
            margin-bottom: 1.5rem;
            padding: 1rem;
            background: rgba(255, 255, 255, 0.03);
            border-radius: 8px;
        }
        
        .step-number {
            width: 30px;
            height: 30px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            flex-shrink: 0;
        }
        
        .step-content {
            flex: 1;
        }
        
        .step-title {
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
        
        .step-description {
            color: #bbb;
        }
        
        /* Contact Support */
        .support-section {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
            border-radius: 16px;
            padding: 2rem;
            margin: 3rem 0;
            text-align: center;
        }
        
        .support-title {
            font-size: 1.5rem;
            font-weight: bold;
            margin-bottom: 1rem;
            color: #667eea;
        }
        
        .support-description {
            color: #bbb;
            margin-bottom: 2rem;
        }
        
        .support-buttons {
            display: flex;
            gap: 1rem;
            justify-content: center;
            flex-wrap: wrap;
        }
        
        .support-button {
            padding: 0.8rem 2rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-decoration: none;
            border-radius: 8px;
            font-weight: 600;
            transition: all 0.3s;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
        }
        
        .support-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        .support-button.secondary {
            background: transparent;
            border: 2px solid #667eea;
            color: #667eea;
        }
        
        /* Stats */
        .stats-section {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 2rem;
            margin: 3rem 0;
        }
        
        .stat-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 1.5rem;
            text-align: center;
        }
        
        .stat-value {
            font-size: 2rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .stat-label {
            color: #888;
            font-size: 0.9rem;
            margin-top: 0.5rem;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .quick-help {
                grid-template-columns: 1fr;
            }
            
            .category-tabs {
                flex-direction: column;
            }
            
            .support-buttons {
                flex-direction: column;
            }
            
            .stats-section {
                grid-template-columns: repeat(2, 1fr);
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Résolution de Problèmes et FAQ</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Centre d'Aide</h1>
          <p className="page-subtitle">Résolution de problèmes et questions fréquemment posées</p>
        </div>
        
        {/* Search Section */}
        <div className="search-section">
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input 
              type="text" 
              className="search-input" 
              placeholder="Rechercher dans l'aide..."
              value={searchTerm}
              onChange={(e) => filterFAQ(e.target.value)}
            />
          </div>
          <div className="search-suggestions">
            <span className="search-tag" onClick={() => setSearch('erreur connexion')}>erreur connexion</span>
            <span className="search-tag" onClick={() => setSearch('performance lente')}>performance lente</span>
            <span className="search-tag" onClick={() => setSearch('authentification')}>authentification</span>
            <span className="search-tag" onClick={() => setSearch('cache redis')}>cache redis</span>
            <span className="search-tag" onClick={() => setSearch('elasticsearch')}>elasticsearch</span>
          </div>
        </div>
        
        {/* Quick Help Cards */}
        <div className="quick-help">
          <div className="help-card" onClick={() => showCategory('installation')}>
            <div className="help-icon">🚀</div>
            <div className="help-title">Installation</div>
            <div className="help-description">Problèmes de déploiement et configuration</div>
          </div>
          
          <div className="help-card" onClick={() => showCategory('performance')}>
            <div className="help-icon">⚡</div>
            <div className="help-title">Performance</div>
            <div className="help-description">Optimisation et latence</div>
          </div>
          
          <div className="help-card" onClick={() => showCategory('errors')}>
            <div className="help-icon">🔧</div>
            <div className="help-title">Erreurs</div>
            <div className="help-description">Messages d'erreur et solutions</div>
          </div>
          
          <div className="help-card" onClick={() => showCategory('api')}>
            <div className="help-icon">📡</div>
            <div className="help-title">API</div>
            <div className="help-description">Utilisation et intégration</div>
          </div>
        </div>
        
        {/* Stats Section */}
        <div className="stats-section">
          <div className="stat-card">
            <div className="stat-value">98.5%</div>
            <div className="stat-label">Problèmes résolus</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">&lt; 2h</div>
            <div className="stat-label">Temps de réponse moyen</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">147</div>
            <div className="stat-label">Articles d'aide</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">24/7</div>
            <div className="stat-label">Support disponible</div>
          </div>
        </div>
        
        {/* Category Tabs */}
        <div className="category-tabs">
          <button 
            className={`tab-button ${activeCategory === 'installation' ? 'active' : ''}`} 
            onClick={() => showCategory('installation')}
          >
            Installation
          </button>
          <button 
            className={`tab-button ${activeCategory === 'performance' ? 'active' : ''}`} 
            onClick={() => showCategory('performance')}
          >
            Performance
          </button>
          <button 
            className={`tab-button ${activeCategory === 'errors' ? 'active' : ''}`} 
            onClick={() => showCategory('errors')}
          >
            Erreurs
          </button>
          <button 
            className={`tab-button ${activeCategory === 'api' ? 'active' : ''}`} 
            onClick={() => showCategory('api')}
          >
            API
          </button>
          <button 
            className={`tab-button ${activeCategory === 'security' ? 'active' : ''}`} 
            onClick={() => showCategory('security')}
          >
            Sécurité
          </button>
        </div>
        
        {/* Installation FAQ */}
        <div className={`faq-section ${activeCategory === 'installation' ? 'active' : ''}`}>
          <div className="faq-container">
            <FAQItem 
              id="install-1" 
              severity="severity-critical"
              question='Docker containers ne démarrent pas - Error: "Cannot connect to the Docker daemon"'
            >
              <h4>Diagnostic</h4>
              <p>Cette erreur indique que le service Docker n'est pas démarré ou que l'utilisateur n'a pas les permissions nécessaires.</p>
              
              <div className="troubleshooting-steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <div className="step-title">Vérifier le statut Docker</div>
                    <div className="step-description">
                      <CodeBlock lang="bash" code="sudo systemctl status docker" />
                    </div>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <div className="step-title">Démarrer Docker si nécessaire</div>
                    <div className="step-description">
                      <CodeBlock lang="bash" code={`sudo systemctl start docker
sudo systemctl enable docker`} />
                    </div>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <div className="step-title">Ajouter l'utilisateur au groupe docker</div>
                    <div className="step-description">
                      <CodeBlock lang="bash" code={`sudo usermod -aG docker $USER
# Puis se reconnecter ou exécuter:
newgrp docker`} />
                    </div>
                  </div>
                </div>
              </div>
            </FAQItem>
            
            <FAQItem 
              id="install-2" 
              severity="severity-warning"
              question="Variables d'environnement non chargées correctement"
            >
              <h4>Solution</h4>
              <p>Vérifiez que le fichier <code>.env</code> est présent et correctement formaté.</p>
              
              <CodeBlock lang="bash" code={`# Vérifier la présence du fichier
ls -la .env*

# Vérifier le contenu
cat .env

# Vérifier le format (pas d'espaces autour du =)
# ✓ Correct: OPENAI_API_KEY=sk-xxxx
# ✗ Incorrect: OPENAI_API_KEY = sk-xxxx`} />
            </FAQItem>
            
            <FAQItem 
              id="install-3" 
              severity="severity-info"
              question="Erreur de port déjà utilisé lors du démarrage"
            >
              <h4>Diagnostic et Solution</h4>
              <p>Un autre processus utilise déjà le port configuré.</p>
              
              <CodeBlock lang="bash" code={`# Identifier le processus utilisant le port 5000
sudo lsof -i :5000

# Arrêter le processus si nécessaire
sudo kill -9 [PID]

# Ou modifier le port dans docker-compose.yml
ports:
  - "5001:5000"  # Utiliser le port 5001 à la place`} />
            </FAQItem>
          </div>
        </div>
        
        {/* Performance FAQ */}
        <div className={`faq-section ${activeCategory === 'performance' ? 'active' : ''}`}>
          <div className="faq-container">
            <FAQItem 
              id="perf-1" 
              severity="severity-critical"
              question="Temps de réponse très lent (> 10 secondes)"
            >
              <h4>Causes Possibles et Solutions</h4>
              
              <div className="troubleshooting-steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <div className="step-title">Vérifier la charge CPU/RAM</div>
                    <div className="step-description">
                      <CodeBlock lang="bash" code={`# Monitoring des ressources
docker stats

# Si CPU > 80% ou RAM > 85%, scaling nécessaire
docker-compose up --scale app=3`} />
                    </div>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <div className="step-title">Optimiser le cache Redis</div>
                    <div className="step-description">
                      <CodeBlock lang="bash" code={`# Vérifier le hit rate du cache
redis-cli info stats | grep keyspace_hits

# Augmenter la TTL si nécessaire
# Dans .env: CACHE_TTL=7200  # 2 heures`} />
                    </div>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <div className="step-title">Optimiser la taille des chunks</div>
                    <div className="step-description">
                      <p>Réduire la taille des chunks peut améliorer les performances:</p>
                      <CodeBlock lang="env" code={`# Dans .env
CHUNK_SIZE=800  # Au lieu de 1000
CHUNK_OVERLAP=150  # Au lieu de 200`} />
                    </div>
                  </div>
                </div>
              </div>
            </FAQItem>
            
            <FAQItem 
              id="perf-2" 
              severity="severity-warning"
              question="Cache Redis utilisation élevée (> 90%)"
            >
              <h4>Solutions</h4>
              <ul>
                <li>Augmenter la mémoire allouée à Redis</li>
                <li>Configurer l'éviction automatique</li>
                <li>Nettoyer les clés expirées</li>
              </ul>
              
              <CodeBlock lang="bash" code={`# Augmenter la mémoire Redis
# Dans docker-compose.yml:
command: redis-server --maxmemory 2gb --maxmemory-policy allkeys-lru

# Nettoyer les clés expirées
redis-cli --scan --pattern "*" | xargs -L 1000 redis-cli del`} />
            </FAQItem>
          </div>
        </div>
        
        {/* Errors FAQ */}
        <div className={`faq-section ${activeCategory === 'errors' ? 'active' : ''}`}>
          <div className="faq-container">
            <FAQItem 
              id="error-1" 
              severity="severity-critical"
              question='Erreur "OpenAI API quota exceeded"'
            >
              <h4>Solutions</h4>
              <div className="troubleshooting-steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <div className="step-title">Vérifier le quota OpenAI</div>
                    <div className="step-description">
                      <p>Allez sur <a href="https://platform.openai.com/usage" style={{ color: '#667eea' }}>https://platform.openai.com/usage</a> pour voir votre utilisation.</p>
                    </div>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <div className="step-title">Configurer le fallback Claude</div>
                    <div className="step-description">
                      <CodeBlock lang="env" code={`# Dans .env
ANTHROPIC_API_KEY=your-claude-api-key
FALLBACK_MODEL=claude-3-sonnet-20240229
ENABLE_FALLBACK=true`} />
                    </div>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <div className="step-title">Optimiser l'utilisation du cache</div>
                    <div className="step-description">
                      <p>Augmenter le cache hit rate pour réduire les appels API:</p>
                      <CodeBlock lang="env" code={`CACHE_TTL=14400  # 4 heures
CACHE_EMBEDDINGS=true
CACHE_SIMILARITY_THRESHOLD=0.95`} />
                    </div>
                  </div>
                </div>
              </div>
            </FAQItem>
            
            <FAQItem 
              id="error-2" 
              severity="severity-critical"
              question='Erreur "Elasticsearch cluster health red"'
            >
              <h4>Diagnostic et Solutions</h4>
              
              <CodeBlock lang="bash" code={`# Vérifier l'état du cluster
curl -X GET "localhost:9200/_cluster/health?pretty"

# Vérifier l'espace disque (ES a besoin de min 15% libre)
df -h

# Redémarrer Elasticsearch si nécessaire
docker-compose restart elasticsearch

# Réinitialiser les index si corrompu
curl -X DELETE "localhost:9200/_all"
python scripts/reindex_documents.py`} />
            </FAQItem>
            
            <FAQItem 
              id="error-3" 
              severity="severity-warning"
              question='Erreur "Neo4j authentication failed"'
            >
              <h4>Solution</h4>
              <p>Vérifiez les credentials Neo4j dans la configuration:</p>
              
              <CodeBlock lang="env" code={`# Dans .env
NEO4J_URI=bolt://localhost:7687
NEO4J_USER=neo4j
NEO4J_PASSWORD=your-password

# Reset du mot de passe si nécessaire
docker exec -it neo4j cypher-shell -u neo4j -p neo4j
CALL dbms.changePassword('nouveau-mot-de-passe');`} />
            </FAQItem>
          </div>
        </div>
        
        {/* API FAQ */}
        <div className={`faq-section ${activeCategory === 'api' ? 'active' : ''}`}>
          <div className="faq-container">
            <FAQItem 
              id="api-1" 
              severity="severity-info"
              question="Comment authentifier les requêtes API?"
            >
              <h4>Authentification JWT</h4>
              <p>Utilisez un token Bearer dans le header Authorization:</p>
              
              <CodeBlock lang="bash" code={`curl -X POST http://localhost:5000/api/v1/query \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"question": "Comment traiter un chèque impayé?"}'`} />
              
              <h4>Obtenir un token</h4>
              <CodeBlock lang="bash" code={`curl -X POST http://localhost:5000/api/v1/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"username": "your-username", "password": "your-password"}'`} />
            </FAQItem>
            
            <FAQItem 
              id="api-2" 
              severity="severity-info"
              question='Rate limiting: "Too many requests"'
            >
              <h4>Limites par Défaut</h4>
              <ul>
                <li>1000 requêtes par heure par token</li>
                <li>10 requêtes par minute par IP</li>
                <li>Headers de réponse: X-RateLimit-*</li>
              </ul>
              
              <h4>Solution</h4>
              <p>Implémentez un système de retry avec backoff exponentiel:</p>
              
              <CodeBlock lang="python" code={`import time
import requests

def query_with_retry(question, max_retries=3):
    for attempt in range(max_retries):
        response = requests.post(url, json={"question": question})
        
        if response.status_code == 200:
            return response.json()
        elif response.status_code == 429:
            # Rate limited, wait and retry
            wait_time = 2 ** attempt  # Exponential backoff
            time.sleep(wait_time)
        else:
            break
    
    raise Exception(f"Failed after {max_retries} attempts")`} />
            </FAQItem>
          </div>
        </div>
        
        {/* Security FAQ */}
        <div className={`faq-section ${activeCategory === 'security' ? 'active' : ''}`}>
          <div className="faq-container">
            <FAQItem 
              id="security-1" 
              severity="severity-critical"
              question="Suspicion d'accès non autorisé détecté"
            >
              <h4>Actions Immédiates</h4>
              
              <div className="troubleshooting-steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <div className="step-title">Vérifier les logs d'audit</div>
                    <div className="step-description">
                      <CodeBlock lang="bash" code={`# Vérifier les tentatives d'authentification échouées
grep "FAILED_LOGIN" /var/log/rag-banking/audit.log

# Vérifier les IPs suspectes
grep "UNAUTHORIZED" /var/log/rag-banking/audit.log | awk '{print $5}' | sort | uniq -c`} />
                    </div>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <div className="step-title">Bloquer les IPs suspectes</div>
                    <div className="step-description">
                      <CodeBlock lang="bash" code={`# Bloquer une IP avec iptables
sudo iptables -A INPUT -s SUSPICIOUS_IP -j DROP

# Ou avec fail2ban
sudo fail2ban-client set rag-banking banip SUSPICIOUS_IP`} />
                    </div>
                  </div>
                </div>
                
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <div className="step-title">Révoquer les tokens compromis</div>
                    <div className="step-description">
                      <CodeBlock lang="bash" code={`# Révoquer tous les tokens d'un utilisateur
python scripts/revoke_user_tokens.py --user suspicious_user

# Forcer la reconnexion de tous les utilisateurs
python scripts/invalidate_all_sessions.py`} />
                    </div>
                  </div>
                </div>
              </div>
            </FAQItem>
          </div>
        </div>
        
        {/* Support Section */}
        <div className="support-section">
          <h2 className="support-title">🆘 Besoin d'Aide Supplémentaire?</h2>
          <p className="support-description">
            Si vous n'avez pas trouvé la solution à votre problème, notre équipe de support est là pour vous aider.
          </p>
          <div className="support-buttons">
            <a href="mailto:support@rag-banking.com" className="support-button">
              📧 Envoyer un Email
            </a>
            <a href="#" className="support-button secondary">
              💬 Chat en Direct
            </a>
            <a href="tel:+33123456789" className="support-button secondary">
              📞 Appeler le Support
            </a>
            <a href="#" className="support-button secondary">
              🎫 Créer un Ticket
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default TroubleshootingFAQ;